import classNames from 'classnames';
import { ComponentProps, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import SearchInput, { type SearchInputProps } from '@alltrails/denali/components/SearchInput';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useSearchIds } from '../../hooks/useSearchIds';
import type { OnSelect, ResultBase } from '../../types/searchBoxTypes';
import { getFirstItem, getNewActiveItem } from '../../utils/resultItemUtils';
import MobileSearch from './MobileSearch';
import CustomSearchResults from './results/CustomSearchResults';
import styles from './styles/FullscreenSearchBox.module.scss';

const searchInstructionsId = 'search-instructions';

type FullscreenSearchBoxProps<T extends ResultBase> = {
  className?: string;
  clearSelectionOnCommit?: boolean;
  commitOnEmptyQuery?: boolean;
  dropdownVariant?: 'default' | 'enveloping' | 'inline';
  hideResults?: boolean;
  iconButtonProps?: SearchInputProps['iconButtonProps'];
  isLoading?: boolean;
  mobileFocusTrapOptions?: ComponentProps<typeof MobileSearch>['focusTrapOptions'];
  onCommit: OnSelect<T>;
  onHideResults?: () => void;
  onInputBlur?: () => void;
  onInputFocus?: () => void;
  onMobileCancel?: () => void;
  onQueryChange: (newQuery: string) => void;
  onShowResults?: () => void;
  placeholder?: string;
  preventScrollOnResultSelection?: boolean;
  query: string;
  renderAdditionalContent?: (query: string | undefined, hideResults: () => void) => ReactNode;
  requireSelection?: boolean;
  size?: SearchInputProps['size'] | 'homepage';
  tertiaryElement?: React.ReactNode;
  testId?: string;
  searchRequestLoading?: boolean;
  validateQuery?: (query: string) => boolean;
} & Pick<SearchInputProps, 'variant'> &
  Pick<ComponentProps<typeof CustomSearchResults<T>>, 'emptyState' | 'renderResultContent' | 'results' | 'searchResultsTabBar'>;

/**
 * An extension off of the Denali `SearchInput` component to be used inline on web & full screen on mobile.
 */
const FullscreenSearchBox = <T extends ResultBase>({
  className,
  clearSelectionOnCommit,
  commitOnEmptyQuery,
  dropdownVariant = 'default',
  emptyState,
  hideResults,
  iconButtonProps,
  isLoading,
  mobileFocusTrapOptions,
  onCommit,
  onHideResults,
  onInputBlur,
  onInputFocus,
  onMobileCancel,
  onQueryChange,
  onShowResults,
  placeholder,
  preventScrollOnResultSelection,
  query,
  renderAdditionalContent,
  renderResultContent,
  requireSelection,
  results,
  searchResultsTabBar,
  searchRequestLoading,
  size = 'md',
  testId,
  tertiaryElement,
  validateQuery,
  variant
}: FullscreenSearchBoxProps<T>) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const [showResults, setShowResults] = useState<boolean>(false);
  const [activeResult, setActiveResult] = useState<T>();

  const isValidQuery = useCallback((value: string) => !validateQuery || validateQuery(value), [validateQuery]);

  const toggleResults = useCallback(
    (show: boolean) => {
      if (show && onShowResults) {
        onShowResults();
      } else if (!show && onHideResults) {
        onHideResults();
      }
      if (!show) {
        setActiveResult(undefined);
      }
      setShowResults(show);
    },
    [onHideResults, onShowResults]
  );

  const onInputClick = useCallback(() => {
    if (isMobile && !showResults && isValidQuery(query)) {
      toggleResults(true);
    }
  }, [isMobile, isValidQuery, query, showResults, toggleResults]);

  const onCancelMobileSearch = useCallback(() => {
    onMobileCancel?.();
    toggleResults(false);
  }, [onMobileCancel, toggleResults]);

  const onBlur = useCallback(() => {
    onInputBlur?.();
    if (!isMobile) {
      toggleResults(false);
    }
    if (commitOnEmptyQuery && !query) {
      onCommit();
    }
  }, [commitOnEmptyQuery, isMobile, onCommit, onInputBlur, query, toggleResults]);

  const onFocus = useCallback(() => {
    onInputFocus?.();
    if (requireSelection) {
      setActiveResult(getFirstItem(results));
    }
    if (!isMobile && isValidQuery(query)) {
      toggleResults(true);
    }
  }, [isMobile, isValidQuery, onInputFocus, query, requireSelection, results, toggleResults]);

  const onChange = useCallback(
    (value: string) => {
      if (!showResults && isValidQuery(value)) {
        toggleResults(true);
      } else if (showResults && !isValidQuery(value)) {
        toggleResults(false);
      }

      onQueryChange(value);
    },
    [isValidQuery, onQueryChange, showResults, toggleResults]
  );

  const onSearchCommit: OnSelect<T> = useCallback(
    (result, additionalInfo) => {
      onCommit(result, additionalInfo);
      toggleResults(false);
      if (clearSelectionOnCommit) {
        setActiveResult(undefined);
      }
    },
    [clearSelectionOnCommit, onCommit, toggleResults]
  );

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLInputElement>) => {
      switch (ev.key) {
        case 'Escape':
          toggleResults(false);
          break;
        case 'Enter':
          if (isMobile && !showResults && isValidQuery(query)) {
            toggleResults(true);
          } else if (activeResult) {
            const index = results.findIndex(result => result === activeResult);
            // If the activeResult is an nls_query, use the query instead of the name
            const resultToCommit = 'type' in activeResult && activeResult.type === 'nls_query' ? { ...activeResult, name: query } : activeResult;
            onSearchCommit(resultToCommit as T, index > -1 ? { index, query } : undefined);
          } else {
            onSearchCommit();
          }
          break;
        case 'ArrowUp':
          ev.preventDefault();
          setActiveResult(getNewActiveItem(results, activeResult, 'prev'));
          break;
        case 'ArrowDown':
          ev.preventDefault();
          setActiveResult(getNewActiveItem(results, activeResult, 'next'));
          break;
        default:
        // do nothing
      }
    },
    [activeResult, isMobile, isValidQuery, onSearchCommit, query, results, showResults, toggleResults]
  );

  useEffect(() => {
    // Avoid some weird focus scenarios if you change to mobile while the results are shown
    if (isMobile) {
      toggleResults(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (hideResults) {
      toggleResults(false);
    }
  }, [hideResults]);

  useEffect(() => {
    if (requireSelection) {
      setActiveResult(getFirstItem(results));
    }
  }, [requireSelection, results, getFirstItem]);

  const renderAdditionalContentEnhanced = useMemo(() => {
    if (renderAdditionalContent) {
      return (q?: string) => {
        return renderAdditionalContent(q, () => toggleResults(false));
      };
    }
    return undefined;
  }, [renderAdditionalContent, toggleResults]);

  const searchIds = useSearchIds();

  return (
    <div className={classNames(styles.container, styles[size], className)} role="search">
      <div id={searchIds.searchLabelId} className="screenReadersOnly">
        {placeholder || 'Search'}
      </div>
      <SearchInput
        className={classNames(styles.input, { [styles.isActive]: showResults && !isMobile })}
        iconButtonProps={iconButtonProps}
        onBlur={onBlur}
        onChange={onChange}
        onClick={onInputClick}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        size={size === 'homepage' ? 'md' : size}
        testId={testId}
        value={query}
        variant={variant}
        searchRequestLoading={searchRequestLoading}
        aria-autocomplete="list"
        aria-controls={searchIds.listboxId}
        aria-haspopup="listbox"
        aria-expanded={showResults}
        aria-activedescendant={activeResult?.id ? searchIds.getOptionId(activeResult.id) : undefined}
        aria-labelledby={searchIds.searchLabelId}
        aria-describedby={searchInstructionsId}
      />
      <div className="screenReadersOnly" id={searchInstructionsId}>
        {intl.formatMessage({
          defaultMessage: 'Begin typing to search, use the up and down arrow keys to navigate, press enter to select'
        })}
      </div>
      {showResults &&
        (isMobile ? (
          <MobileSearch
            activeId={activeResult?.id}
            areResultsLoading={isLoading}
            emptyState={emptyState}
            iconButtonProps={iconButtonProps}
            focusTrapOptions={mobileFocusTrapOptions}
            onCancel={onCancelMobileSearch}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onSelect={onSearchCommit}
            placeholder={placeholder}
            query={query}
            renderAdditionalContent={renderAdditionalContentEnhanced}
            renderResultContent={renderResultContent}
            results={results}
            searchResultsTabBar={searchResultsTabBar}
            testId={`${testId}-mobile`}
            listboxId={searchIds.listboxId}
            getOptionId={searchIds.getOptionId}
          />
        ) : (
          <CustomSearchResults
            activeId={activeResult?.id}
            areResultsLoading={isLoading}
            className={classNames(styles.dropdown, styles[dropdownVariant])}
            emptyState={emptyState}
            onSelect={onSearchCommit}
            preventScrollOnResultSelection={preventScrollOnResultSelection}
            query={query}
            renderAdditionalContent={renderAdditionalContentEnhanced}
            renderResultContent={renderResultContent}
            results={results}
            searchResultsTabBar={searchResultsTabBar}
            searchIds={searchIds}
          />
        ))}
      {tertiaryElement}
    </div>
  );
};

export default FullscreenSearchBox;
